import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
} from "reactstrap";
import BreadCrumb from "../../components/common/breadCrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { digitRegex, emailRegex, InputPlaceHolder, phoneRegex, RequiredField, SelectPlaceHolder, validationMessages } from "../../utils/helpers/validationMasseges";
import TableContainer from "../../components/common/tableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "../../components/common/deleteModal";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Loader from "../../components/common/loader";
import { listOfData } from "../../api/commonApi";
import {
  addClientDetails,
  deleteClientDetails,
  editClientDetails, listOfClientDetails
} from "../../api/companyApi";
import {
  LIST_OF_CLIENT_DESIGNATION,
} from "../../utils/helpers/enums";
import { toast } from "react-toastify";
import NoResultsMessage from "../../components/noResultMessage";
import { checkStatusCodeSuccess, CommonLabel, HandleResponse } from "../../utils/helpers/common";
import { BdeLabel } from "../../common/constant/Bde";
import BaseButton from "../../components/BaseComponents/BaseButton";
import BaseInput from "../../base/baseInput";
import { BaseSelect } from "../../components/BaseComponents/BaseSelect";

const ClientManagement = () => {
  document.title = BdeLabel.ClientInformation;
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [listOfCompanyData, setListOfCompanyData] = useState();
  const [editCompanyData, setEditCompanyData] = useState();
  const [selectedCompanyId, setSelectedCompanyId] = useState();
  const [selectedOsType, setSelectedOsType] = useState();
  const [selectedClientDetails, setSelectedClientDetails] = useState();
  const [selectedCompanyData, setSelectedCompanyData] = useState();
  const [isClientId, setIsClientId] = useState();
  const navigate = useNavigate();

  const fetchData = () => {
    setTableLoader(true);
    listOfData({
      modelName: "companyModel",
      condition: { isDeleted: 0 },
      selectionCriteria: ["id", "companyName"],
    })
      .then((res) => {
        setListOfCompanyData(res?.data?.map((status) => ({
          value: status.id,
          label: status.companyName,
        })));
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setTableLoader(false);
      });

      listOfClientDetails().then((res) => {
        if (res?.data?.data) {
          setSelectedClientDetails(res?.data?.data)
          const data = res?.data?.data?.map((index) => {
            return {
              label: index.company.companyName,
              value: index.companyId
            }
          })
          setSelectedCompanyData(data)
        } else {
          toast.error(res?.data?.message);
        }
      }).catch((err) => {
        return err;
      })
      .finally(() => {
        setTableLoader(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const projectColumns = useMemo(() => [
    {
      Header: CommonLabel.SrNo,
      Cell: ({ row }) => {
        const indexFromStart = row?.index + 1;
        return <div>{indexFromStart}</div>;
      },
    },
    {
      Header: BdeLabel.CompanyName,
      accessor: "company.companyName",
      filterable: false,
    },
    {
      Header: BdeLabel.ClientName,
      accessor: "clientName",
      filterable: false,
    },
    {
      Header: BdeLabel.ClientDesignation,
      accessor: "clientDesignation",
      filterable: false,
    },
    {
      Header: BdeLabel.Email,
      accessor: "email",
      filterable: false,
      Cell: (cellProps) => {
        return (
          <div>
            {cellProps?.cell?.row?.original?.email || HandleResponse.NoData}
          </div>
        );
      },
    },
    {
      Header: BdeLabel.PhoneNo,
      accessor: "phoneNo",
      filterable: false,
      Cell: (cellProps) => {
        return (
          <div>
            {cellProps?.cell?.row?.original?.phoneNo || HandleResponse.NoData}
          </div>
        );
      },
    },
    {
      Header: CommonLabel.Action,
      Cell: (cellProps) => {
        return (
          <div>
            <div className="hstack gap-2">
              <Link
                id={`editMode-${cellProps?.cell?.row?.index}`}
                className="btn btn-sm btn-soft-info remove-list"
                onClick={() =>
                  editCompanyDetailsClick(cellProps?.cell?.row?.original)
                }
              >
                <i className="ri-pencil-fill align-bottom" />
                <ReactTooltip
                  place="bottom"
                  variant="info"
                  content="Edit"
                  anchorId={`editMode-${cellProps?.cell?.row?.index}`}
                />
              </Link>
              <Link
                id={`deleteMode-${cellProps.cell.row.index}`}
                className="btn btn-sm btn-soft-danger remove-list"
                onClick={() =>
                  companyDelete(cellProps?.cell?.row?.original?.id)
                }
              >
                <i className="ri-delete-bin-5-fill align-bottom" />
                <ReactTooltip
                  place="bottom"
                  variant="error"
                  content="Delete"
                  anchorId={`deleteMode-${cellProps?.cell?.row?.index}`}
                />
              </Link>
            </div>
          </div>
        );
      },
    },
  ]);

    const editCompanyDetailsClick = (cellProps) => {
      setEditCompanyData(cellProps);
      setIsClientId(cellProps?.companyId);
      setIsEdit(true);
      navigate("/project-management/");
    };

  const companyDelete = (companyId) => {
    setSelectedCompanyId(companyId);
    setDeleteModal(true);
  };

  const companyDeleteTodo = () => {
    setLoading(true);
    setDeleteModal(false);
    deleteClientDetails(selectedCompanyId, { isDeleted: true })
      .then((res) => {
        if (checkStatusCodeSuccess(res?.data?.statusCode)) {
          fetchData();
          toast.success(res?.data?.message);
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      companyId: editCompanyData?.companyId || null,
      clientName: editCompanyData?.clientName || "",
      clientDesignation: editCompanyData?.clientDesignation || "",
      email: editCompanyData?.email || "",
      phoneNo: editCompanyData?.phoneNo || "",
    },
    validationSchema: Yup.object({
      companyId: Yup.string().required(validationMessages.required(BdeLabel.CompanyName)),
      clientName: Yup.string().required(validationMessages.required(BdeLabel.ClientName)),
      clientDesignation: Yup.string().required(
        validationMessages.required(BdeLabel.ClientDesignation)
      ),
      email: Yup.string().notRequired()
        .matches(emailRegex, validationMessages.format(BdeLabel.Email)),
      phoneNo: Yup.string().notRequired()
        .matches(
          phoneRegex,
          validationMessages.contactLength(BdeLabel.PhoneNo, 10)
        ),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        setLoading(true);
        setIsEdit(false);
        editClientDetails(editCompanyData?.id, { ...values })
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              fetchData();
              validation.resetForm();
              setEditCompanyData(null);
              toast.success(res?.data?.message);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(true);
        addClientDetails({...values})
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              fetchData();
              validation.resetForm();
              setEditCompanyData(null);
              toast.success(res?.data?.message);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
  });
  
  let filtered;
  if (isClientId !== undefined) {
    filtered = selectedCompanyData?.filter((item) => {
      return isClientId === item.value
    })
  }

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => companyDeleteTodo()}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        {loading === false ? (
          <Container fluid>
            <BreadCrumb
              title="Client Information"
              pageTitle="Company information"
              navigation="/company-management"
            />
            <Row className="mb-2">
              <div className="col-sm order-3 order-sm-2 mt-3 mt-sm-0">
                <h5 className="fw-semibold mb-0 mt-2">
                  {BdeLabel.ClientInformation}
                </h5>
              </div>

              <div className="col-auto order-2 order-sm-3 ms-auto">
                <div className="hstack gap-2">
                  <Link
                    to={isEdit ? "/project-management" : "/company-management"}
                  >
                    <BaseButton color="primary" type="button"
                      onClick={() => {
                        setIsEdit(false);
                        validation.resetForm();
                        setEditCompanyData(null);
                       }}>
                      {CommonLabel.Back}
                    </BaseButton>
                  </Link>
                </div>
              </div>
            </Row>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  <Row>
                    <Col lg={4} md={4} sm={6}>
                      <div className="mb-3">
                        <BaseSelect
                          name="companyId"
                          label={BdeLabel.CompanyName}
                          placeholder={SelectPlaceHolder(BdeLabel.CompanyName)}
                          value={validation.values.companyId}
                          options={listOfCompanyData}
                          handleChange={(field, value) => {
                            setSelectedOsType(value);
                            validation.setFieldValue(field, value);
                          }}
                          handleBlur={validation.handleBlur}
                          error={validation.errors.companyId}
                          touched={validation.touched.companyId}
                          required
                        />
                      </div>
                    </Col>

                    <Col lg={4} md={4} sm={6}>
                      <div className="mb-3">
                        <BaseInput
                          name="clientName"
                          type="text"
                          placeholder={InputPlaceHolder(BdeLabel.ClientName)}
                          label={BdeLabel.ClientName}
                          onChange={validation.handleChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.clientName}
                          touched={validation.touched.clientName}
                          error={validation.errors.clientName}
                          required
                        />
                      </div>
                    </Col>

                    <Col lg={4} md={4} sm={6}>
                      <div className="mb-3">
                        <BaseSelect
                          name="clientDesignation"
                          label={BdeLabel.ClientDesignation}
                          placeholder={SelectPlaceHolder(
                            BdeLabel.ClientDesignation
                          )}
                          options={LIST_OF_CLIENT_DESIGNATION}
                          value={validation.values.clientDesignation}
                          handleChange={(field, value) =>
                            validation.setFieldValue(field, value)
                          }
                          handleBlur={validation.handleBlur}
                          error={validation.errors.clientDesignation}
                          touched={validation.touched.clientDesignation}
                          required
                        />
                      </div>
                    </Col>

                    <Col lg={4} md={4} sm={6}>
                      <div className="mb-3">
                        <BaseInput
                          name="phoneNo"
                          type="text"
                          label={BdeLabel.PhoneNo}
                          placeholder={InputPlaceHolder(BdeLabel.PhoneNo)}
                          onChange={(e) => {
                            if (digitRegex.test(e.target.value)) {
                              validation.handleChange(e);
                            }
                          }}
                          handleBlur={validation.handleBlur}
                          value={validation.values.phoneNo}
                          touched={validation.touched.phoneNo}
                          error={validation.errors.phoneNo}
                          maxLength={10}
                          required={false}
                        />
                      </div>
                    </Col>

                    <Col lg={4} md={4} sm={6}>
                      <div className="mb-3">
                        <BaseInput
                          name="email"
                          type="email"
                          label={BdeLabel.Email}
                          placeholder={InputPlaceHolder(BdeLabel.Email)}
                          onChange={validation.handleChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.email}
                          touched={validation.touched.email}
                          error={validation.errors.email}
                          required={false}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={12}>
                      <div className="hstack gap-2 justify-content-end">
                        <BaseButton type="submit" color="primary">
                          {isEdit === false
                            ? CommonLabel.Submit
                            : CommonLabel.Update}
                        </BaseButton>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Form>

            {isEdit === false && (
              <Card>
                <CardHeader className="align-items-center d-flex">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {BdeLabel.ClientInformation}
                  </h4>
                </CardHeader>
                <CardBody>
                  {selectedClientDetails?.length || tableLoader ? (
                    <TableContainer
                      columns={projectColumns}
                      data={selectedClientDetails || []}
                      isAddUserList={false}
                      customPageSize={5}
                      isGlobalFilter={true}
                      tableLoader={tableLoader}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted"
                    />
                  ) : (
                    <NoResultsMessage />
                  )}
                </CardBody>
              </Card>
            )}
          </Container>
        ) : (
          <Loader />
        )}
      </div>
    </>
  );
};

export default ClientManagement;
